import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Free-sample auto-remove guard (phase 2 — plan
 * tasks/features/2026-07-14-lg-cart-drawer.md §Phase 2).
 *
 * Mirror of the Hydrogen `manageManualFreeSamples` server hook: when the
 * sample gate is closed (no paid lines, or paid subtotal below
 * `samples_min_subtotal`) but `_free_sample` lines are still in the cart,
 * `snippets/lg-cart-samples.liquid` renders this element carrying the sample
 * line keys. Connecting (initial render or any §4.5 morph) zeroes those lines
 * via /cart/update.js and morphs the drawer with the response — after which
 * the re-rendered fragment no longer contains a guard, so the cycle
 * terminates. A per-signature cooldown stops a retry loop if the server keeps
 * returning the same violation (e.g. network/permission failure).
 */

const RETRY_COOLDOWN_MS = 3000;

/** @type {Map<string, number>} */
const lastAttempt = new Map();

class LgCartSamplesGuard extends HTMLElement {
  static observedAttributes = ['data-keys'];

  connectedCallback() {
    this.#attempt();
  }

  // Morphs UPDATE a persisting guard in place rather than re-inserting it, so
  // connectedCallback alone would leave a failed removal stranded until full
  // reload — re-attempt when the key set changes (Gate-2 review WARN 2).
  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.#attempt();
  }

  #attempt() {
    const keys = (this.dataset.keys || '').split(',').filter(Boolean);
    if (!keys.length) return;

    const signature = keys.join(',');
    const now = Date.now();
    const previous = lastAttempt.get(signature);
    if (previous !== undefined && now - previous < RETRY_COOLDOWN_MS) return;
    lastAttempt.set(signature, now);

    this.#removeSampleLines(keys);
  }

  /** @param {string[]} keys - line-item keys of the `_free_sample` lines to remove. */
  async #removeSampleLines(keys) {
    // Same sections-param collection as component-cart-items.js updateQuantity.
    const sectionsToUpdate = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    // action 'remove' — cart-drawer.js only auto-opens on 'add', so this
    // updates the cart bubble without re-opening a closed drawer.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: keys.map((id) => ({ id, quantity: 0 })),
        promise: deferred.promise,
      })
    );

    try {
      const updates = Object.fromEntries(keys.map((key) => [key, 0]));
      const body = JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();

      // Resolve WITH the vendor `detail.sections` shape (§4.15) and do NOT morph
      // here: this guard renders INSIDE <cart-items-component> (via the
      // cart_items_children capture in lg-cart-drawer.liquid), so its dispatched
      // CartLinesUpdateEvent is caught by cart-items-component#handleCartUpdate,
      // which performs the single hydration morph. The previous code resolved
      // {cart} only AND self-morphed, so the component ALSO ran its
      // sections-absent cache:false fallback refetch — the double render §4.15
      // documents. Mirrors component-cart-items.js:271-280.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          items: data.items,
          itemCount: data.item_count,
          source: 'lg-cart-samples',
          didError: false,
        },
      });
    } catch (error) {
      // Reject like component-cart-items.js:254 — resolving {cart: null} would
      // zero the header bubble while the lines are still in the cart (WARN 1).
      deferred.reject(error);
      console.error('lg-cart-samples-guard: failed to remove sample lines', error);
    }
  }
}

if (!customElements.get('lg-cart-samples-guard')) {
  customElements.define('lg-cart-samples-guard', LgCartSamplesGuard);
}
